import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {BehaviorSubject, combineLatest, Observable, throwError} from 'rxjs';
import {catchError, debounceTime, filter, map, take} from 'rxjs/operators';

import {getAuthenticatedUser, getProviderApplications, State} from '@app/app.reducers';
import {User} from '@app/core/model/user';
import {environment} from '@env/environment';
import {AuthenticationService} from '@app/core/service/authentication.service';
import * as authAction from '@app/views/common/auth.actions';
import {ApplicationProviderListModel} from '@app/views/admin/application-settings/models/application-provider-list.model';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {copyData} from '@app/core/model/integration-models';

@Injectable({
    providedIn: 'root',
})
export class AnnouncementService {

    currUser: User;
    onInputEvents: EventEmitter<InputEvent> = new EventEmitter<InputEvent>();
    onChangeClaimEvents: EventEmitter<ChangeCliemEvent> = new EventEmitter<ChangeCliemEvent>();
    onDialogEvents: BehaviorSubject<DialogEvent> = new BehaviorSubject<DialogEvent>(null);

    allClaimAcceptedEvents = new BehaviorSubject<boolean>(false);
    public allClaimAcceptedEvents$ = this.allClaimAcceptedEvents.asObservable();

    isCheckingClaimsForAuthentication = new BehaviorSubject<boolean>(false);
    isClaimingAnnouncementsIsInProgress = new BehaviorSubject<boolean>(false);

    actionCallbacksForInputEvent: Map<EventNames, CallbackEventConfig> = new Map<EventNames, CallbackEventConfig>();
    policyConf: Map<EventNames, PolicyConfig> = new Map<EventNames, PolicyConfig>();

    constructor(
        private authService: AuthenticationService,
        private authenticatedUserService: AuthenticatedUserService,
        private http: HttpClient,
        private store: Store<State>,
        private router: Router
    ) {
        this.store.pipe(select(getAuthenticatedUser))
            .subscribe(user => {
                this.currUser = user;

                if (!user) {
                    this.allClaimAcceptedEvents.next(false);
                }
            });

        this.onChangeClaimEvents.pipe(
            filter((event: ChangeCliemEvent) =>
                event && event.claim && (['accepted', 'rejected', 'error'].indexOf(event.type) > -1)
            ),
            debounceTime(500),
            map((event: ChangeCliemEvent) =>
                [event, this.policyConf.get(event.name)]
            ),
            filter(([event, conf]: [ChangeCliemEvent, PolicyConfig]) => !!conf)
        )
            .subscribe(([event, conf]: [ChangeCliemEvent, PolicyConfig]) => {
                if (event && event.name === EventNames.Authentication && event.type == 'error' && event.claim.isRequired()) {
                    this.store.dispatch(new authAction.SignOutAction());
                }

                if (event && event.name === EventNames.Authentication && event.claim && conf.claimsForDialog.length > 0) {
                    const claimIndexInDialog = conf.claimsForDialog.findIndex(dialogClaim => dialogClaim.id == event.claim.id && dialogClaim.providerApplicationGuid == event.claim.providerApplicationGuid);

                    this.toggleShowContent((claimIndexInDialog + 1 >= conf.claimsForDialog.length) && conf.isAllRequiredAccepted());
                } else {
                    this.toggleShowContent(conf.isAllRequiredAccepted());
                }
            });

        this.setCallbacks([EventNames.Notification, EventNames.Authentication, EventNames.ClaimRequest], {
            delivered: (event: InputEvent) => {

            },
            accepted: (event: InputEvent) => {

            },
            rejected: (event: InputEvent) => {
                const claim: Claim = event.claim;
                if (claim.rule.rejectCommand) {
                    if (claim.rule.rejectCommand === 'logout') {
                        this.store.dispatch(new authAction.SignOutAction());
                    }
                    if (claim.rule.rejectCommand === 'warn-message') {
                        this.onDialogEvents.next({
                            type: 'extinfo',
                            name: EventNames.Authentication,
                            message: 'Until EULA and Privacy Policy are signed, some restrictions on the functionality of the System may be imposed.',
                        });
                    }
                }
            }
        });
        /* Observable.interval(180 * 1000).subscribe(x => {
            if (this.currUser) {
                this.checkClaimsForNotifications().subscribe(d => {
                    // TODO
                });
            }
        });*/

        this.onInputEvents.subscribe((event: InputEvent) => {
            if (this.currUser) {
                const cbConf: CallbackEventConfig = this.actionCallbacksForInputEvent.get(event.name);
                const pConf: PolicyConfig = this.policyConf.get(event.name);

                switch (event.eventName ) {
                    case 'DialogOpened': {
                        const claim: Claim = event.claim;
                        if (!claim.isDelivered()) {
                            this.markAsDelivered(claim.name, claim.version, claim.rule.id, this.getClaimAttributes(claim) || pConf.attributes, claim.providerApplicationGuid)
                                .subscribe(res => {
                                    if (pConf) {
                                        pConf.putClaim(res.claims[0]);
                                    }

                                    if (cbConf && cbConf.delivered) {
                                        cbConf.delivered(event);
                                    }

                                    this.onChangeClaimEvents.emit({
                                        type: 'delivered',
                                        claim: res.claims[0],
                                        name: event.name
                                    });
                                });
                        }
                        break;
                    }
                    case 'DialogClosed': {

                        break;
                    }
                    case 'ClaimAccepted': {
                        if (event.claim.rule) {
                            const claim: Claim = event.claim;
                            // let allowedSign = true;
                            // if (claim.rule.acceptanceOrgLevel) {
                            //    allowedSign = this.currUser.primaryAdmin;
                            // }
                            // if (allowedSign) {
                            this.markAsAccepted(claim.name, claim.version, claim.rule.id,
                                this.getClaimAttributes(claim) || pConf.attributes, claim.providerApplicationGuid, event.eventSource)
                                .subscribe(res => {
                                    if (pConf) {
                                        pConf.putClaim(res.claims[0]);
                                    }
                                    if (event.claim.providerApplicationGuid) {
                                        res.claims[0].providerApplicationGuid = event.claim.providerApplicationGuid;
                                    }

                                    if (cbConf && cbConf.accepted) {
                                        cbConf.accepted(event);
                                    }

                                    this.onChangeClaimEvents.next({
                                        type: 'accepted',
                                        claim: res.claims[0],
                                        name: event.name
                                    });

                                    this.allClaimAcceptedEvents.next((res.claims.length > 0 || !this.currUser.primaryAdmin) && res.isAllAccepted());
                                }, error => {
                                    this.onChangeClaimEvents.emit({
                                        type: 'error',
                                        claim: claim,
                                        name: event.name
                                    });
                                });
                        }
                        break;
                    }
                    case 'ClaimRejected': {
                        if (event.claim.rule) {
                            const claim: Claim = event.claim;
                            this.markAsRejected(claim.name, claim.version, claim.rule.id, this.getClaimAttributes(claim) || pConf.attributes, claim.providerApplicationGuid)
                                .subscribe(res => {
                                    if (pConf) {
                                        pConf.putClaim(res.claims[0]);
                                    }
                                    if (event.claim.providerApplicationGuid) {
                                        res.claims[0].providerApplicationGuid = event.claim.providerApplicationGuid;
                                    }

                                    if (cbConf && cbConf.rejected) {
                                        cbConf.rejected(event);
                                    }

                                    this.onChangeClaimEvents.emit({
                                        type: 'rejected',
                                        claim: res.claims[0],
                                        name: event.name
                                    });
                                }, error => {
                                    this.onChangeClaimEvents.emit({
                                        type: 'error',
                                        claim: claim,
                                        name: event.name
                                    });
                                });
                        }
                        break;
                    }
                    case 'ExtInfoOpened': {
                        this.store.dispatch(new authAction.HideContentAction());
                        break;
                    }
                    case 'ExtInfoClosed': {
                        this.store.dispatch(new authAction.SignOutAction());
                        break;
                    }
                }
            }
        });
    }

    setCallbacks(eventNames: EventNames[], callbacks: any) {
        eventNames.forEach(caseName => {
            this.actionCallbacksForInputEvent.set(caseName, callbacks);
        });
    }

    getPrivacyPolicyForFooter() {
        this.checkClaimsForClaimRequest('gg4l.connect.privacy.policy').subscribe((conf: PolicyConfig) => {
            if (conf.claims && conf.claims.length > 0) {
                this.onDialogEvents.next({
                    type: 'announcement',
                    name: EventNames.ClaimRequest,
                    message: null
                });
            }
        });
    }

    getTermsOfUseForFooter() {
        this.checkClaimsForClaimRequest('gg4l.connect.terms.use').subscribe((conf: PolicyConfig) => {
            if (conf.claims && conf.claims.length > 0) {
                this.onDialogEvents.next({
                    type: 'announcement',
                    name: EventNames.ClaimRequest,
                    message: null
                });
            }
        });
    }

    checkClaimsForNotifications(): Observable<PolicyConfig> {
        return this.sendRequest({
            eventName: EventNames.Notification
        }).pipe(map((conf: PolicyConfig) => {
            conf.eventName = EventNames.Notification;
            this.policyConf.set(EventNames.Notification, conf);
            return conf;
        }));
    }

    checkClaimsForAuthentication(): Observable<PolicyConfig> {
        return combineLatest(
            this.sendRequest({eventName: EventNames.Authentication}),
            this.store.pipe(
                select(getProviderApplications),
                filter((applications: ApplicationProviderListModel[]) =>
                    !!applications || !this.authenticatedUserService.isProvider()
                ),
                take(1)
            )
        ).pipe(map(([conf, providerApplications]: [PolicyConfig, ApplicationProviderListModel[]]) => {
            conf.eventName = EventNames.Authentication;
            conf.claims = conf.claims.filter(claim => !claim.isAccepted() && (!claim.isRejected() || claim.isRequired()));

            if (this.authenticatedUserService.isProvider()) {
                conf.claims = conf.claims.map((claim: Claim) => {
                    if (claim.providerApplicationGuid) {
                        const providerApp = providerApplications.find(app => app.guid == claim.providerApplicationGuid);
                        claim.defaultApplicationGuid = providerApp.defaultApplicationGuid;
                    }

                    return claim;
                });
            }
            this.policyConf.set(EventNames.Authentication, conf);
            return conf;
        }));
    }

    checkClaimsForClaimRequest(claimName: string = 'gg4l.connect.privacy.policy'): Observable<PolicyConfig> {
        return this.sendRequest({
            eventName: EventNames.ClaimRequest,
            claimName: claimName
        }).pipe(map((conf: PolicyConfig) => {
            conf.eventName = EventNames.ClaimRequest;
            this.policyConf.set(EventNames.ClaimRequest, conf);
            return conf;
        }));
    }

    checkClaimsForVendorApplication(applicationGuid: string): Observable<PolicyConfig> {
        return this.sendRequest({
            eventName: EventNames.DataAccess,
            claimName: `vendor.policy.${applicationGuid}`,
            attributes: {
                application: applicationGuid
            }
        }).pipe(map((conf: PolicyConfig) => {
            conf.eventName = EventNames.DataAccess;
            conf.attributes = {application: applicationGuid};
            this.policyConf.set(EventNames.DataAccess, conf);
            return conf;
        }));
    }

    checkClaimsForProviderApplication(claimName: string, applicationGuid: string): Observable<PolicyConfig> {
        return this.sendRequest({
            eventName: EventNames.DataAccess,
            claimName: claimName,
            application: applicationGuid,
            attributes: {application: this.getAppGuidFromClaimName(claimName)}
        }).pipe(map((conf: PolicyConfig) => {
            conf.eventName = EventNames.DataAccess;
            this.policyConf.set(EventNames.DataAccess, conf);

            return conf;
        }));
    }

    markAsDelivered(claimName: string, claimVersion: string, ruleId: number, attributes: any = {}, providerApplicationGuid?: string): Observable<PolicyConfig> {
        return this.sendRequest({
            eventName: 'ClaimDelivered',
            ruleId: ruleId,
            claimName: claimName,
            claimVersion: claimVersion,
            attributes: attributes,
            application: providerApplicationGuid || null
        });
    }

    markAsAccepted(claimName: string,
        claimVersion: string,
        ruleId: number,
        attributes: any = {},
        providerApplicationGuid?: string,
        eventSource?: EventSource
    ): Observable<PolicyConfig> {
        return this.sendRequest({
            eventName: 'AcceptClaim',
            ruleId: ruleId,
            claimName: claimName,
            claimVersion: claimVersion,
            attributes: attributes,
            application: providerApplicationGuid || null,
            eventSource: eventSource || null
        });
    }

    markAsRejected(claimName: string, claimVersion: string, ruleId: number, attributes: any = {}, providerApplicationGuid?: string): Observable<PolicyConfig> {
        return this.sendRequest({
            eventName: 'RejectClaim',
            ruleId: ruleId,
            claimName: claimName,
            claimVersion: claimVersion,
            attributes: attributes,
            application: providerApplicationGuid || null
        });
    }

    private toggleShowContent(value: boolean) {
        if (value) {
            this.store.dispatch(new authAction.ShowContentAction());
        } else {
            this.store.dispatch(new authAction.HideContentAction());
        }
    }

    private sendRequest(req: any): Observable<PolicyConfig> {
        const url = `${environment.datahubBaseURI}/api/v2/announcement/event`;
        return this.http
            .post<PolicyConfig>(url, req)
            .pipe(
                map(json => {
                    const conf: PolicyConfig = new PolicyConfig();
                    conf.fromJSON(json);
                    conf.claims = json.claims.map(cjs => new Claim().fromJSON(cjs));
                    return conf;
                }),
                catchError(error => this.handleError(error))
            );
    }

    getClaimAttributes(claim: Claim) {
        const applicationGuid = this.getAppGuidFromClaimName(claim.name) || claim.rule.attrValue || claim['defaultApplicationGuid'];

        return applicationGuid ? { application: applicationGuid } : null;
    }

    getAppGuidFromClaimName(claimName: string): string {
        const resultMatch = claimName.match(new RegExp(`[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12}`));
        return resultMatch ? resultMatch[0] : null;
    }


    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.message}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }
}

export enum EventNames {
    Notification = 'Notification',
    Authentication = 'Authentication',
    ClaimRequest = 'ClaimRequest',
    DataAccess = 'DataAccess'
}

export class Serializable {
    fromJSON(json) {
        for (const propName in json) {
            this[propName] = json[propName];
        }
        return this;
    }
}

export class ClaimRule extends Serializable {
    id: number;
    description: string;
    claimName: string;
    active: boolean;
    acceptanceRequired: boolean;
    acceptanceOrgLevel: boolean;
    attrKey: string;
    attrValue: string;
    rejectCommand: string;
}
export class Claim extends Serializable {
    id: number;
    title: string;
    name: string;
    version: string;
    regDate: Date;
    body: string;
    internalFormat: string;
    hasOrgAcceptance: boolean;
    hasDeliveries: boolean;
    userConfirmations: any[];
    status: number;
    rule: ClaimRule;
    ownerGuid: string;
    providerApplicationGuid: string;
    defaultApplicationGuid?: string;

    isRequired() {
        return this.rule && this.rule.acceptanceRequired;
    }
    isDelivered() {
        return this.userConfirmations.filter(cf => cf.type == 'DELIVERY').length > 0;
    }
    isAccepted() {
        let res = this.userConfirmations.filter(cf => cf.type == 'ACCEPTANCE').length > 0;
        if (this.rule && this.rule.acceptanceOrgLevel) {
            res = res || this.hasOrgAcceptance;
        }
        return res;
    }
    isRejected() {
        return this.userConfirmations.filter(cf => cf.type == 'REJECT').length > 0;
    }
}
export class PolicyConfig extends Serializable {
    hasOrgLevelDocuments: boolean;
    confirmedAllOrgLevelDocuments: boolean;
    claims: Claim[];
    commands: string[];
    eventName: EventNames;
    attributes: any;
    claimIndex = 0;
    claimsForDialog: Claim[] = [];

    getNextClaim(): Claim {
        if (this.claimIndex > this.claimsForDialog.length - 1) {
            return;
        }
        return this.claimsForDialog[this.claimIndex++];
    }

    reset(): void {
        this.claimIndex = 0;
        // for Authentication show not accepted documents only
        if ([EventNames.Authentication].indexOf(this.eventName) > -1) {
            this.claimsForDialog = this.claims.filter(c => !c?.isAccepted());
        } else {
            this.claimsForDialog = this.claims;
        }
    }

    getTotalClaims(): number {
        return this.claimsForDialog.length;
    }

    putClaim(claim: Claim) {
        let replaced = false;
        for (let i = 0; i < this.claims.length; i++) {
            if (this.claims[i].id === claim.id) {
                this.claims[i] = claim;
                replaced = true;
                break;
            }
        }
        if (!replaced) {
            this.claims.push(claim);
        }
    }

    isAllAccepted() {
        return this.claims.filter(c => !c.isAccepted()).length === 0;
    }

    isAllRequiredAccepted() {
        return this.claims.filter(c => c.isRequired() && !c.isAccepted()).length === 0;
    }
}
export class InputEvent {
    eventName: string;
    claim: Claim;
    name: EventNames;
    eventSource?: EventSource;
}
export class DialogEvent {
    type: string;
    name: EventNames;
    message: string;
}
export class ChangeCliemEvent {
    type: string;
    claim: Claim;
    name: EventNames;
}
type InputEventCallback = (e: InputEvent) => any;
type DialogEventCallback = (e: DialogEvent) => any;
type ChangeClaimEventCallback = (e: ChangeCliemEvent) => any;

export class CallbackEventConfig {
    delivered: InputEventCallback;
    accepted: InputEventCallback;
    rejected: InputEventCallback;
}

export enum EventSource {
    REQUEST = 'REQUEST',
    LOGIN = 'LOGIN',
    INTEGRATION_VIEW = 'INTEGRATION_VIEW'
}
